import { AppBook, AppQuestion } from '../types/utils';

export interface Page {
  page_id: string;
  chapter_id: string;
  questions: any[] | string;
}

export interface QuizQuestion {
  id: string;
  question: string;
  type: 'true_false' | 'multiple_choice';
  options?: string[];
  correctAnswer?: string | boolean;
  explanation?: string;
}

export interface QuizConfig {
  seed: number;
  questionCount: number;
  chapter: string;
  book: string;
  questionType: 'true_false' | 'multiple_choice';
}

export interface QuestionSelectionResult {
  questions: QuizQuestion[];
  totalAvailable: number;
  selectedCount: number;
}

/**
 * Seeded random number generator for reproducible quiz generation
 */
class SeededRandom {
  private seed: number;

  constructor(seed: number) {
    this.seed = seed;
  }

  next(): number {
    this.seed = (this.seed * 9301 + 49297) % 233280;
    return this.seed / 233280;
  }

  shuffle<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }
}

/**
 * QuizQuestionSelector handles the logic for selecting and processing quiz questions
 */
export class QuizQuestionSelector {
  private questions: Page[];
  private multipleChoiceQuestions: Page[];
  private book?: AppBook;

  constructor(questions: Page[], multipleChoiceQuestions: Page[]);
  constructor(book: AppBook);
  constructor(questionsOrBook: Page[] | AppBook, multipleChoiceQuestions?: Page[]) {
    if (Array.isArray(questionsOrBook)) {
      // Legacy constructor for backward compatibility
      this.questions = questionsOrBook;
      this.multipleChoiceQuestions = multipleChoiceQuestions || [];
    } else {
      // New constructor using AppBook
      this.book = questionsOrBook;
      this.questions = [];
      this.multipleChoiceQuestions = [];
    }
  }

  /**
   * Select questions based on configuration
   */
  selectQuestions(config: QuizConfig): QuestionSelectionResult {
    if (this.book) {
      return this.selectQuestionsFromBook(config);
    } else {
      return this.selectQuestionsLegacy(config);
    }
  }

  /**
   * Select questions using the new AppBook structure
   */
  private selectQuestionsFromBook(config: QuizConfig): QuestionSelectionResult {
    if (!this.book) throw new Error('Book not available');

    if (config.chapter === 'all') {
      // For "all chapters" - select questions from each chapter separately
      const selectedQuestions: AppQuestion[] = [];
      let totalAvailable = 0;
      
      // Get all chapter IDs
      const chapterIds = Object.keys(this.book.chapters);
      
      // Calculate how many questions to take from each chapter
      const questionsPerChapter = Math.ceil(config.questionCount / chapterIds.length);
      
      // Track questions by text to avoid duplicates across chapters
      const questionTextSet = new Set<string>();
      
      // Select questions from each chapter
      chapterIds.forEach(chapterId => {
        const chapter = this.book!.chapters[chapterId];
        const questions = config.questionType === 'true_false' 
          ? chapter.trueFalseQuestions 
          : chapter.multipleChoiceQuestions;
        
        // Randomize questions within this chapter
        const random = new SeededRandom(config.seed + parseInt(chapterId)); // Different seed per chapter
        const shuffledQuestions = random.shuffle(questions);
        
        // Take questions from this chapter, avoiding duplicates
        let chapterQuestionsAdded = 0;
        for (const question of shuffledQuestions) {
          if (chapterQuestionsAdded >= questionsPerChapter) break;
          
          // Check if this question text is already in our set
          if (!questionTextSet.has(question.question)) {
            questionTextSet.add(question.question);
            selectedQuestions.push(question);
            chapterQuestionsAdded++;
          }
        }
        
        totalAvailable += questions.length;
      });
      
      // Take only the requested number of questions
      const finalQuestions = selectedQuestions.slice(0, config.questionCount);
      
      // Convert AppQuestion to QuizQuestion format
      const processedQuestions = finalQuestions.map((q, index) => {
        let options: string[] = [];
        let correctAnswer: string | boolean = q.correctAnswer;
        
        if (q.type === 'true_false') {
          options = Array.isArray(q.options) ? q.options : ['True', 'False'];
        } else if (q.type === 'multiple_choice') {
          if (Array.isArray(q.options)) {
            options = q.options;
          } else if (q.options && typeof q.options === 'object' && !Array.isArray(q.options)) {
            // Convert {A: "Red", B: "Blue", C: "Green", D: "Yellow"} to ["Red", "Blue", "Green", "Yellow"]
            const optionsRecord = q.options as Record<string, string>;
            options = ['A', 'B', 'C', 'D'].map(letter => optionsRecord[letter]).filter(Boolean);
            
            // Convert letter answer to actual text (A -> "Red")
            if (typeof q.correctAnswer === 'string' && q.correctAnswer.match(/^[A-D]$/)) {
              const answerText = optionsRecord[q.correctAnswer];
              if (answerText) {
                correctAnswer = answerText;
              }
            }
          }
        }
        
        return {
          id: q.id,
          question: q.question,
          type: q.type,
          options,
          correctAnswer,
          explanation: q.explanation
        };
      });

      return {
        questions: processedQuestions,
        totalAvailable,
        selectedCount: processedQuestions.length
      };
    } else {
      // Single chapter - existing logic works fine
      const chapter = this.book.chapters[config.chapter];
      if (chapter) {
        const questions = config.questionType === 'true_false' 
          ? chapter.trueFalseQuestions 
          : chapter.multipleChoiceQuestions;
        
        const random = new SeededRandom(config.seed);
        const shuffledQuestions = random.shuffle(questions);
        
        // Deduplicate questions by text even within a single chapter
        const questionTextSet = new Set<string>();
        const uniqueQuestions: AppQuestion[] = [];
        
        for (const question of shuffledQuestions) {
          if (!questionTextSet.has(question.question)) {
            questionTextSet.add(question.question);
            uniqueQuestions.push(question);
          }
        }
        
        console.log(`Chapter ${config.chapter}: ${questions.length} total questions, ${uniqueQuestions.length} unique questions after deduplication`);
        
        const selectedQuestions = uniqueQuestions.slice(0, config.questionCount);
        console.log(`Requested ${config.questionCount} questions, selected ${selectedQuestions.length} questions`);
        
        // Convert AppQuestion to QuizQuestion format
        const processedQuestions = selectedQuestions.map((q, index) => {
          let options: string[] = [];
          let correctAnswer: string | boolean = q.correctAnswer;
          
          if (q.type === 'true_false') {
            options = Array.isArray(q.options) ? q.options : ['True', 'False'];
          } else if (q.type === 'multiple_choice') {
            if (Array.isArray(q.options)) {
              options = q.options;
            } else if (q.options && typeof q.options === 'object' && !Array.isArray(q.options)) {
              // Convert {A: "Red", B: "Blue", C: "Green", D: "Yellow"} to ["Red", "Blue", "Green", "Yellow"]
              const optionsRecord = q.options as Record<string, string>;
              options = ['A', 'B', 'C', 'D'].map(letter => optionsRecord[letter]).filter(Boolean);
              
              // Convert letter answer to actual text (A -> "Red")
              if (typeof q.correctAnswer === 'string' && q.correctAnswer.match(/^[A-D]$/)) {
                const answerText = optionsRecord[q.correctAnswer];
                if (answerText) {
                  correctAnswer = answerText;
                }
              }
            }
          }
          
          return {
            id: q.id,
            question: q.question,
            type: q.type,
            options,
            correctAnswer,
            explanation: q.explanation
          };
        });

        return {
          questions: processedQuestions,
          totalAvailable: questions.length,
          selectedCount: processedQuestions.length
        };
      }
    }
    
    return { questions: [], totalAvailable: 0, selectedCount: 0 };
  }

  /**
   * Legacy question selection for backward compatibility
   */
  private selectQuestionsLegacy(config: QuizConfig): QuestionSelectionResult {
    // Filter questions by type and chapter
    const relevantQuestions = this.getRelevantQuestions(config.questionType);
    const filteredQuestions = this.filterByChapter(relevantQuestions, config.chapter);

    if (filteredQuestions.length === 0) {
      return {
        questions: [],
        totalAvailable: 0,
        selectedCount: 0
      };
    }

    // Randomize and select questions
    const random = new SeededRandom(config.seed);
    const shuffledQuestions = random.shuffle(filteredQuestions);
    const selectedQuestions = shuffledQuestions.slice(0, config.questionCount);

    // Process and normalize questions
    const processedQuestions = selectedQuestions.map((q, index) => 
      this.processQuestion(q, index, config.questionType)
    );

    return {
      questions: processedQuestions,
      totalAvailable: filteredQuestions.length,
      selectedCount: processedQuestions.length
    };
  }

  /**
   * Get questions relevant to the specified type
   */
  private getRelevantQuestions(questionType: 'true_false' | 'multiple_choice'): Page[] {
    return questionType === 'true_false' ? this.questions : this.multipleChoiceQuestions;
  }

  /**
   * Filter questions by chapter
   */
  private filterByChapter(questions: Page[], chapter: string): Page[] {
    return chapter === 'all' 
      ? questions 
      : questions.filter(q => q.chapter_id === chapter);
  }

  /**
   * Process a single question and normalize its format
   */
  private processQuestion(questionData: Page, index: number, questionType: 'true_false' | 'multiple_choice'): QuizQuestion {
    const questions = this.parseQuestionData(questionData.questions);
    
    if (!questions || questions.length === 0) {
      throw new Error(`No questions found in page ${questionData.page_id}`);
    }

    const question = questions[0]; // Take the first question from each page
    const options = this.extractOptions(question, questionType);
    const correctAnswer = this.extractCorrectAnswer(question, questionType, options);

    return {
      id: `${questionData.page_id}_${index}_0`,
      question: question.question,
      type: questionType,
      options: options,
      correctAnswer: correctAnswer,
      explanation: question.explanation
    };
  }

  /**
   * Parse question data, handling both string and array formats
   */
  private parseQuestionData(questions: any[] | string): any[] {
    if (typeof questions === 'string') {
      try {
        return JSON.parse(questions);
      } catch (error) {
        throw new Error(`Failed to parse question data: ${error}`);
      }
    }
    return questions;
  }

  /**
   * Extract options from question data based on type
   */
  private extractOptions(question: any, questionType: 'true_false' | 'multiple_choice'): string[] {
    if (questionType === 'true_false') {
      // True/False questions have options as array
      if (!Array.isArray(question.options)) {
        throw new Error('True/False question must have options as array');
      }
      return question.options;
    } else {
      // Multiple choice questions have options as object, convert to array in correct order
      if (!question.options || typeof question.options !== 'object') {
        throw new Error('Multiple choice question must have options as object');
      }
      return ['A', 'B', 'C', 'D'].map(letter => {
        if (!question.options[letter]) {
          throw new Error(`Missing option ${letter} in multiple choice question`);
        }
        return question.options[letter];
      });
    }
  }

  /**
   * Extract and normalize correct answer
   */
      private extractCorrectAnswer(question: any, questionType: 'true_false' | 'multiple_choice', options: string[]): string | boolean {
      // Handle both old and new field names during transition
      const correctAnswer = question.correctAnswer !== undefined ? question.correctAnswer : 
                           question.correct_answer !== undefined ? question.correct_answer : 
                           question.answer;
    
    if (correctAnswer === undefined || correctAnswer === null) {
      throw new Error('Question must have a correct answer');
    }

    if (questionType === 'true_false') {
      // Normalize boolean values
      if (typeof correctAnswer === 'string') {
        return correctAnswer.toLowerCase() === 'true';
      }
      return correctAnswer as boolean;
    } else {
      // For multiple choice, validate the answer exists in options
      if (typeof correctAnswer === 'string') {
        const answerIndex = correctAnswer.charCodeAt(0) - 65; // A=0, B=1, C=2, D=3
        if (answerIndex >= 0 && answerIndex < options.length) {
          return options[answerIndex];
        }
      }
      throw new Error(`Invalid correct answer for multiple choice question: ${correctAnswer}`);
    }
  }

  /**
   * Get available question counts for a chapter
   */
  getAvailableQuestionCount(chapter: string, questionType: 'true_false' | 'multiple_choice'): number {
    if (this.book) {
      // New AppBook structure
      if (chapter === 'all') {
        // Count questions across all chapters
        let totalCount = 0;
        Object.values(this.book.chapters).forEach(chapterData => {
          const questions = questionType === 'true_false' 
            ? chapterData.trueFalseQuestions 
            : chapterData.multipleChoiceQuestions;
          totalCount += questions.length;
        });
        return totalCount;
      } else {
        // Count questions for specific chapter
        const chapterData = this.book.chapters[chapter];
        if (chapterData) {
          const questions = questionType === 'true_false' 
            ? chapterData.trueFalseQuestions 
            : chapterData.multipleChoiceQuestions;
          return questions.length;
        }
        return 0;
      }
    } else {
      // Legacy format
      const relevantQuestions = this.getRelevantQuestions(questionType);
      const filteredQuestions = this.filterByChapter(relevantQuestions, chapter);
      return filteredQuestions.length;
    }
  }

  /**
   * Get total available questions across all chapters
   */
  getTotalAvailableQuestions(questionType: 'true_false' | 'multiple_choice'): number {
    const relevantQuestions = this.getRelevantQuestions(questionType);
    return relevantQuestions.length;
  }

  /**
   * Validate quiz configuration
   */
  validateConfig(config: QuizConfig): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (config.questionCount <= 0) {
      errors.push('Question count must be greater than 0');
    }

    if (config.seed < 0) {
      errors.push('Seed must be non-negative');
    }

    const availableCount = this.getAvailableQuestionCount(config.chapter, config.questionType);
    if (availableCount === 0) {
      errors.push(`No questions available for chapter ${config.chapter} with type ${config.questionType}`);
    } else if (config.questionCount > availableCount) {
      // Allow the quiz to start with fewer questions than requested
      console.log(`Warning: Requested ${config.questionCount} questions but only ${availableCount} available for chapter ${config.chapter}. Quiz will use ${availableCount} questions.`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
} 